import React, { useCallback, useMemo } from "react";
import _ from "lodash";
import { useFindingDetails } from "./FindingDetailsProvider";
import TicketsCreationAPI from "restAPI/TicketsCreationAPI";
import ModalUtil from "utils/modalUtil";
import ConfirmPopup from "components/ConfirmPopup";
import message from "components/CustomMessage";
import { getTicketStatus, isTicketCreationFailed } from "../utils";
import { CellRenderers } from "utils/tableUtils";
import { TICKET_SYSTEM_TYPE_DISPLAY_TEXT } from "constants/TicketContants";
import { MinusCircleOutlined } from "@ant-design/icons";
import { PermissionSetMap } from "constants/RBACConstants";
import ActionMenuV2 from "components/ActionMenuV2";
import { CollapseHeaderDivider } from "../FindingDetailsModalV2";
import { LoadingIcon } from "components/Icons";
import { Collapse, Table } from "antd";

export function FindingDetailsTickets({ designV3 = true }) {
    const { id, ticketDetails, fetchTickets } = useFindingDetails();

  const detachTicket = useCallback(
    (data) => {
      if (data?.armorcodeTicketId) {
        ModalUtil.show({
          content: (
            <ConfirmPopup
              title={`Are you sure want to detach ticket ?`}
              okText="Yes"
              note
              noteLength={1000}
              onOk={async (note) => {
                await TicketsCreationAPI.detachJiraTicketForFinding({
                  findingId: [id],
                  note,
                  ticketId: [data.ticketDetailJpaDto.ticketId],
                }).promise.then(() => {
                  message.success("Ticket detachment done successfully.");
                });
              }}
            />
          ),
          afterClose: fetchTickets,
        });
      }
    },
    [id, fetchTickets]
  );

  const onActionClick = useCallback(
    (name, record) => {
      switch (record.name) {
        case "detachTicket":
          detachTicket(record.data);
          break;
        default:
          break;
      }
    },
    [detachTicket]
  );

  const columns = useMemo(() => {
    return [
      {
        title: "Ticket ID",
        dataIndex: "armorcodeTicketId",
        render: (value, record) => getTicketStatus(record),
      },
      {
        title: "Ticket Status",
        dataIndex: "ticketDetailJpaDto",
        render: (value, record) =>
          CellRenderers.VALUE_OR_NA(
            record.ticketDetailJpaDto?.latestStatus ||
              record.armorcodeTicketStatus
          ),
      },
      {
        title: "Source",
        dataIndex: "ticketSystem",
        render: (value) =>
          CellRenderers.VALUE_OR_NA(TICKET_SYSTEM_TYPE_DISPLAY_TEXT[value]),
      },
      {
        title: "Action",
        dataIndex: "armorcodeTicketId",
        render: (role, record) => {
          const isDetachTicketDisabled = isTicketCreationFailed(
            record.status || record.latestStatus
          );
          const items = [
            {
              type: "detachTicket",
              text: "Detach Ticket",
              icon: <MinusCircleOutlined />,
              permission: PermissionSetMap.DELETE_TICKETS,
              disabled: isDetachTicketDisabled,
            },
          ];
          return (
            <ActionMenuV2 items={items} onClick={onActionClick} data={record} />
          );
        },
        width: 60,
      },
    ];
  }, [onActionClick]);

  const loading = _.isUndefined(ticketDetails);
  const noTickets = _.isEmpty(ticketDetails);

  const header = (
    <div className="d-flex align-items-center">
      TICKETS
      <CollapseHeaderDivider />
      {loading ? (
        <LoadingIcon />
      ) : noTickets ? (
        "None"
      ) : (
        CellRenderers.PLUS_MORE(ticketDetails.map((t) => getTicketStatus(t)))
      )}
    </div>
  );

  const ticketSection = loading ? (
    <div className="p-m text-center">
      <LoadingIcon />
    </div>
  ) : noTickets ? (
    <div className="p-m text-center">No Ticket Information Available</div>
  ) : (
    <Table
      dataSource={ticketDetails}
      columns={columns}
      size={"small"}
      pagination={{ hideOnSinglePage: true }}
    />
  );

  return (
    <>
      {designV3 ? (
        ticketSection
      ) : (
        <Collapse className="collapse-v2">
          <Collapse.Panel header={header}>{ticketSection}</Collapse.Panel>
        </Collapse>
      )}
    </>
  );
}

export const TicketsTabTitle = () => {
    const { ticketDetails } = useFindingDetails();
    const loading = _.isUndefined(ticketDetails);
    const header = (
        <div className="d-flex align-items-center">
          Tickets{' '}
          {loading ? (
            <LoadingIcon />
          ) : <>({ticketDetails.length})</>}
        </div>
      );
    return header;
}
